<template lang="pug">
.addUser
  //- Barra de navegación
  van-nav-bar(
    :title="fullName"
    left-arrow
    @click-left="close"
    @click-right="saveUser")
    van-icon(name="save" slot="right" v-if="!userLogged")

  .main.padding-double-bottom

    .van-cell-group__title {{ $t("users.dataTitle") }}
    van-cell-group

      //- Email input
      van-field(
        type="email"
        name="email"
        v-model="email"
        v-validate="'required|email'"
        :data-vv-as="$t('users.email')"
        :label="$t('users.email')"
        :class="{'error': errors.has('email') }"
        :placeholder="$t('users.email')"
        :disabled="!!user")

      //- Advanced input
      van-switch-cell(
        v-model="advanced"
        :title="$t('users.advanced')"
        :disabled="userLogged"
        :class="{disabled: userLogged}"
        @change="setAdvancedUser")

    //- Errores de validación del formulario
    .auth__error.padding-halve-top.padding-halve-bottom.bg-white(v-show="errors.has('email')")
      | {{ errors.first('email') }}

    .van-cell-group__title
      span(v-if="advanced") {{ $t("users.allUnits") }}
      span(v-if="!advanced") {{ $t("users.selectUnitTitle") }}

    .users__units(
      :class="{disabled: advanced || userLogged}")

      //- Listado de dispositivos
      .units__device__bar(
        v-for="(device, mac) in installation.devices"
        :key="mac")
        .units__device__name
          van-icon.media__figure(:name="GetDeviceIcon(device)")
          | {{ device.name}}
        .units__device__action
          .checkbox
            input(
              type="checkbox"
              name="devices"
              v-model="devices"
              :value="mac"
              :disabled="advanced || userLogged")
            label

      //- Errores de validación del formulario
      .auth__error.padding-halve-top.padding-halve-bottom(v-if="devicesEmpty && !cleanForm")
        | {{ $t('new.schedule.errorDevicesEmpty') }}

    //- Botones
    .button-bottom-group
      .button-bottom.text-danger(
        v-if="!userLogged && user"
        @click="confirmDeleteUser") {{ $t('schedule.delete') }}
</template>

<script>
import i18n from '@/locales/i18n';
import { getErrorId } from '@/services/error.service';
import GetDeviceIcon from '@/mixins/GetDeviceIcon.mixin';
import { USER_TYPE } from '@/app.config';
import { mapGetters } from 'vuex';
import UsersService from '../../services/users.service';

export default {
  mixins: [GetDeviceIcon],
  data() {
    return {
      email: '',
      advanced: false,
      devices: [],
      cleanForm: true,
      isLoading: false,
      usersError: null,
    };
  },
  props: {
    installationID: {
      type: String,
      default: '',
    },
    user: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapGetters({
      userData: 'userModule/getUser',
      getInstallation: 'unitsModule/getInstallation',
    }),

    /**
     * Devuelve los datos de la instalación almacenados en el Store
     *
     * @return {Installation} - Datos del dispositivo
     */
    installation() {
      return this.getInstallation(this.installationID);
    },

    /**
     * Devuelve el nombre completo del usuario cuando lo editamos,
     * o un título genérico cuando lo vamos a crear
     */
    fullName() {
      return this.user ? `${this.user.name} ${this.user.lastName}` : i18n.t('users.addTitle');
    },

    /**
     * Un usuario no puede cambiarse los permisos a si mismo,
     * esta propiedad devuelve si el usuario a editar es el propio usuario logueado
     */
    userLogged() {
      if (!this.user) return false;
      return this.userData.email === this.user.email;
    },

    /**
     * Indica si se ha seleccionado algún dispositivo
     */
    devicesEmpty() {
      return !this.devices.length;
    },
  },
  methods: {
    /**
     * Alterna entre el tipo básico y avanzado
     *
     * Cuando es avanzado añade todos los dispositivos a la lista de dispositivos autorizados,
     * si lo pasamos a básico, se eliminan todos los dispositivos
     */
    setAdvancedUser() {
      this.cleanForm = false;
      this.devices = [];

      if (this.advanced) {
        Object.keys(this.installation.devices).forEach(mac => this.devices.push(mac));
      }
    },

    /**
     * Salva los datos de un usuario
     *
     * Comprueba si los datos del formulario son válidos
     * Comprueba si se trata de una instalación nueva o de una edición
     */
    saveUser() {
      this.cleanForm = false;

      this.$validator.validateAll().then(result => {
        if (result && !this.devicesEmpty && !this.userLogged) {
          if (this.user) {
            this.editUser();
          } else {
            this.addUser();
          }
        }
      });
    },

    /**
     * Invita a un nuevo usuario
     */
    async addUser() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.usersError = null;

        // Preparo los datos
        const user = {
          installation_id: this.installationID,
          email: this.email,
          type: this.advanced ? USER_TYPE.ADVANCED : USER_TYPE.BASIC,
          lang: i18n.locale,
          permitted_devices: this.devices,
        };

        await UsersService.addUserAuthorization(user);

        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.add.title'),
            message: i18n.t('users.add.message'),
          })
          .then(() => {
            this.close();
          });
      } catch (error) {
        // Obtengo el error
        this.usersError = getErrorId(error);

        this.$toast.clear();

        switch (this.usersError) {
          case 'userNotConfirmed':
            this.$dialog
              .alert({
                title: i18n.t('new.users.userInvitedNotConfirmed.title'),
                message: i18n.t('new.users.userInvitedNotConfirmed.message'),
              })
              .then(() => {
                this.close();
              });
            break;
          case 'installationAlreadyAdded':
            this.$dialog
              .alert({
                title: i18n.t('new.users.installationAlreadyAdded.title'),
                message: i18n.t('new.users.installationAlreadyAdded.message'),
              })
              .then(() => {
                this.close();
              });
            break;
          default:
            this.$dialog
              .alert({
                title: i18n.t('users.fails.title'),
                message: i18n.t('users.fails.message'),
              })
              .then(() => {
                this.close();
              });
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Edita los permisos de un usuario
     */
    async editUser() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.usersError = null;

        // Preparo los datos
        const user = {
          installation_id: this.installationID,
          user_id: this.user._id,
          type: this.advanced ? USER_TYPE.ADVANCED : USER_TYPE.BASIC,
          permitted_devices: this.devices,
        };

        await UsersService.editUserAuthorization(user);
        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.edit.title'),
            message: i18n.t('users.edit.message'),
          })
          .then(() => {
            this.$emit('updateUser', { installationID: this.installationID, user });
          });
      } catch (error) {
        // Obtengo el error
        this.usersError = getErrorId(error);

        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.fails.title'),
            message: i18n.t('users.fails.message'),
          })
          .then(() => {
            this.$emit('close');
          });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Confirma si realmente quiere eliminar al usuario
     */
    confirmDeleteUser() {
      this.$dialog
        .confirm({
          title: i18n.t('users.deleteConfirm.title'),
          message: i18n.t('users.deleteConfirm.message'),
        })
        .then(() => {
          this.deleteUser();
        });
    },

    /**
     * Elimina a un usuario
     */
    async deleteUser() {
      try {
        // Compruebo si hay una petición en curso,
        // si la hay, no hago nada
        if (this.isLoading) return;

        // Muestro el loader
        this.$toast.loading({
          duration: 0,
          mask: true,
          message: i18n.t('general.loading'),
        });

        // Inicializo valores
        this.isLoading = true;
        this.usersError = null;

        // Preparo los datos
        const user = {
          installation_id: this.installationID,
          user_id: this.user._id,
        };

        await UsersService.deleteUserAuthorization(user);
        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.delete.title'),
            message: i18n.t('users.delete.message'),
          })
          .then(() => {
            this.$emit('deleteUser', { installationID: this.installationID, user });
          });
      } catch (error) {
        // Obtengo el error
        this.usersError = getErrorId(error);

        this.$toast.clear();

        this.$dialog
          .alert({
            title: i18n.t('users.fails.title'),
            message: i18n.t('users.fails.message'),
          })
          .then(() => {
            this.$emit('close');
          });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Cierra este componente
     */
    close() {
      this.$emit('close');
    },
  },

  created() {
    /**
     * Si estamos editando un usuario, asigno los valores
     */
    if (this.user) {
      this.email = this.user.email;
      this.advanced = this.user.type === USER_TYPE.ADVANCED;

      if (this.user.type === USER_TYPE.ADVANCED) {
        Object.keys(this.installation.devices).forEach(mac => this.devices.push(mac));
      } else {
        this.devices = this.user.permitted_devices;
      }
    }
  },
};
</script>
